import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { enquiryTypes, products } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const inputCls =
  "w-full bg-transparent border-0 border-b border-gray-300 py-3 text-sm text-oe-navy placeholder:text-gray-400 focus:outline-none focus:border-oe-navy transition-colors";

export default function ContactSection() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    enquiry: "",
    model: "",
    firstName: "",
    lastName: "",
    company: "",
    position: "",
    email: "",
    country: "",
    message: "",
    agree: false,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.agree) {
      toast({
        title: "Please complete the form",
        description: "First name, email and accepting the terms are required.",
      });
      return;
    }
    // MOCK submit — stored to localStorage only for now
    const saved = JSON.parse(localStorage.getItem("oe_enquiries") || "[]");
    saved.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("oe_enquiries", JSON.stringify(saved));
    toast({
      title: "Request sent",
      description: "Thank you — our team will reply as soon as we're able.",
    });
    setForm({
      enquiry: "", model: "", firstName: "", lastName: "", company: "",
      position: "", email: "", country: "", message: "", agree: false,
    });
  };

  return (
    <section id="contact" className="bg-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="text-oe-red font-semibold text-sm uppercase tracking-wider mb-3">Contact us</p>
            <h2 className="oe-heading text-3xl md:text-4xl text-oe-navy mb-5">How can we help you?</h2>
            <p className="text-muted-foreground leading-relaxed max-w-md">
              Please fill in your contact details below and we'll reply as soon as we're able.
            </p>
            <img
              src="https://images.unsplash.com/photo-1591200834528-4050ce99fe78?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Ocean Energy facility"
              className="mt-10 rounded-lg w-full h-64 object-cover hidden lg:block"
            />
          </div>

          <form onSubmit={submit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <select
                className={inputCls}
                value={form.enquiry}
                onChange={(e) => update("enquiry", e.target.value)}
              >
                <option value="">How can we help you?</option>
                {enquiryTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                className={inputCls}
                value={form.model}
                onChange={(e) => update("model", e.target.value)}
              >
                <option value="">Model Select</option>
                {products.map((p) => (
                  <option key={p.model} value={p.model}>{p.model}</option>
                ))}
              </select>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <input className={inputCls} placeholder="First Name*" value={form.firstName} onChange={(e) => update("firstName", e.target.value)} />
              <input className={inputCls} placeholder="Last Name" value={form.lastName} onChange={(e) => update("lastName", e.target.value)} />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <input className={inputCls} placeholder="Company" value={form.company} onChange={(e) => update("company", e.target.value)} />
              <input className={inputCls} placeholder="Position" value={form.position} onChange={(e) => update("position", e.target.value)} />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <input className={inputCls} placeholder="Email*" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
              <input className={inputCls} placeholder="Country" value={form.country} onChange={(e) => update("country", e.target.value)} />
            </div>
            <textarea
              className={`${inputCls} resize-none`}
              rows={3}
              placeholder="Message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
            />
            <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={form.agree}
                onChange={(e) => update("agree", e.target.checked)}
                className="mt-1 accent-[#16265c] w-4 h-4"
              />
              <span>I agree to the terms of service and privacy policy.</span>
            </label>
            <button
              type="submit"
              className="group inline-flex items-center gap-3 oe-navy text-white font-semibold px-8 py-4 rounded-md hover:oe-navy-2 transition-colors"
            >
              Send Request
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
